import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { adminApi } from "../../api";

const PAGE_SIZE = 25;

/**
 * List, filters, pagination, generate-action for Media Summaries.
 * Side note: `processingIds` is exposed so the detail modal can disable Regenerate.
 */
export default function useMediaSummariesList() {
  const [tipo, setTipo] = useState("AU");
  const [hasResumo, setHasResumo] = useState("false");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, stats: { grand_total: 0, com_resumo: 0 } });
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.summaryList({
        tipo, has_resumo: hasResumo || undefined, q,
        page, page_size: PAGE_SIZE,
      });
      setData(r);
    } catch (e) {
      toast.error(e.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [tipo, hasResumo, q, page]);

  useEffect(() => { load(); }, [tipo, hasResumo, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    setPage(1);
    load();
  }, [load]);

  const generate = useCallback(async (id, force = false) => {
    if (processingIds.has(id)) return;
    setProcessingIds((prev) => new Set(prev).add(id));
    const t = toast.loading(`A gerar resumo (ID ${id})… pode demorar 30-90s`);
    try {
      const res = await adminApi.summaryGenerate(id, force);
      if (res.ok) {
        toast.success("Resumo gerado com sucesso!", { id: t });
        await load();
      } else if (res.status === "skipped") {
        toast.info(`Já existe resumo (${res.reason}). Use 'Regenerar' para substituir.`, { id: t });
      } else {
        toast.error(`Falhou: ${res.error || "erro desconhecido"}`, { id: t, duration: 10000 });
      }
    } catch (e) {
      toast.error(e.message || "Erro a gerar", { id: t, duration: 10000 });
    } finally {
      setProcessingIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }, [processingIds, load]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return {
    tipo, setTipo, hasResumo, setHasResumo, q, setQ,
    page, setPage, totalPages, pageSize: PAGE_SIZE,
    data, loading, processingIds,
    load, onSearch, generate,
  };
}
